import { MDIIcon } from 'pages/icons/MDIIcons';
import { useMemo } from 'react';
import { Column, SortingRule } from 'react-table';
import { getDefaultSizePerPageList } from 'utils';
import { PageSize } from './Pagination';
import { Table } from './Table';
import { ServerPageParams, TableResult } from './useServerPagedData';

interface Props {
    tableResult: TableResult<any>;
    dataLoading: boolean;
    serverPageParams: ServerPageParams;
    setServerPageParams: (params: ServerPageParams) => void;

    isSearchable?: boolean;
    isSortable?: boolean;
    pagination?: boolean;
    isSelectable?: boolean;
    isExpandable?: boolean;
    columns: ReadonlyArray<Column>;
    pageSize?: number;
    searchBoxClass?: string;
    tableClass?: string;
    theadClass?: string;
    Actions?: JSX.Element;
    paginationPosition?: 'top' | 'bottom' | 'both';
    setSelectedRowIds?: (selectedRows: number[]) => void;
    renderRowSubComponent?: (value?: any) => any;
    isToggleExpandedRows?: boolean;
    isExcludeAllPaginationSize?: boolean;
    expandAllRows?: boolean;
}

const ServerPagedTable = (props: Props) => {
    const { tableResult, dataLoading, serverPageParams, setServerPageParams, ...otherProps } = props;

    const onChangePage = (value: string) => {
        setServerPageParams({
            ...serverPageParams,
            pageNumber: value,
        });
    };

    const onChangePageSize = (value: string) => {
        setServerPageParams({
            ...serverPageParams,
            perPage: value,
            pageNumber: '0',
        });
    };

    const toggleSortBy = (sortingRules: SortingRule<{}>[]) => {
        const rule = sortingRules[0];
        if (!rule) return;

        setServerPageParams({
            ...serverPageParams,
            sortProperty: rule.id,
            sortType: rule.desc ? 'desc' : 'asc',
        });
    };

    const pageCount = useMemo(() => {
        return Math.ceil(tableResult.total / parseInt(serverPageParams.perPage || '10'));
    }, [serverPageParams, tableResult]);

    const sizePerPageList: PageSize[] = useMemo(
        () => getDefaultSizePerPageList(tableResult.result, tableResult.total, props.isExcludeAllPaginationSize),
        [tableResult]
    );

    const pageIndex: number = useMemo(
        () => parseInt(serverPageParams.pageNumber || '0'),
        [serverPageParams.pageNumber]
    );

    return (
        <div className="position-relative">
            {dataLoading && (
                <div
                    className="d-flex justify-content-center align-items-center position-absolute h-100 w-100"
                    style={{ backgroundColor: 'rgba(0,0,0,.05)' }}>
                    <MDIIcon icon="loading" size={48} spin className="primary-color" />
                </div>
            )}

            <div style={{ pointerEvents: dataLoading ? 'none' : 'all' }}>
                <Table
                    data={tableResult.result}
                    sizePerPageList={sizePerPageList}
                    manualPagination
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    totalCount={tableResult.total}
                    onChangePage={onChangePage}
                    onChangePageSize={onChangePageSize}
                    manualSortBy
                    toggleSortBy={toggleSortBy}
                    renderRowSubComponent={props.renderRowSubComponent}
                    isToggleExpandedRows={props.isToggleExpandedRows}
                    expandAllRows={props.expandAllRows}
                    {...otherProps}
                />
            </div>
        </div>
    );
};

export default ServerPagedTable;